import { Component, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { countryOfBusiness, regionLabels, statesFor } from 'src/app/_data/country-states';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { GlobalService } from 'src/app/_services/global.service';

export type TaxBasis = 'shipping' | 'origin';
export type NexusType = 'Physical' | 'Economic';

export interface StateTaxRate {
  id: number;
  state: string;
  taxBasis: TaxBasis;
  nexusType: NexusType;
  nexusNote?: string;
  category: string;
  rate: number;
  active: boolean;
}

/**
 * "Before you continue" disclaimer block + acknowledgement checkbox is hidden for now. The
 * component and code are kept intact — flip to `true` to bring it back. TODO re-enable tax disclaimer.
 */
export const TAX_DISCLAIMER_ENABLED = false;

@Component({
  selector: 'app-tax-setting-v2',
  templateUrl: './tax-setting-v2.component.html',
  styleUrls: ['./tax-setting-v2.component.scss']
})
export class TaxSettingV2Component implements OnInit {

  // Exposed for the template — see TAX_DISCLAIMER_ENABLED above. TODO re-enable tax disclaimer.
  disclaimerEnabled = TAX_DISCLAIMER_ENABLED;

  /** The business's country decides the list: US states, or Pakistan's provinces (rates are typed by hand there). */
  country = countryOfBusiness(GlobalService.userInfo?.businessSetup?.[0], GlobalService.countryCode);
  usStates = statesFor(this.country) as any[];
  labels = regionLabels(this.country);
  /** "state" or "province" in running text. */
  get region(): string { return this.labels.state.toLowerCase(); }
  categories = [
    'All categories',
    'Food, beverages & tobacco',
    'Clothing & footwear',
    'Digital goods & services',
  ];

  collectTax = true;
  // The selector here is the single source of truth for which section/table is shown — rows are
  // tagged with the basis they were added under so switching back and forth doesn't lose data,
  // but the UI always shows ONE table at a time (shipping vs. origin are different tax bases and
  // showing them merged was confusing — see visibleStateRates()).
  taxBasis: TaxBasis = 'shipping';
  disclaimerAcknowledged = false;

  // Backed by the TaxRate table (GetTaxRate/CreateTaxRate/UpdateTaxRate/DeleteTaxRate) — each row
  // persists immediately on Add/Edit/Remove rather than waiting for the page's Save button.
  stateRates: StateTaxRate[] = [];
  isLoadingStateRates = false;

  // State name -> default tax rate %, loaded from GetTaxRateDefault. Used to suggest a rate when
  // a state is picked in the Add/Edit modal — never overrides a rate the merchant already typed.
  taxRateDefaultsByState: { [state: string]: number } = {};
  isLoadingTaxRateDefaults = false;

  showAddRateModal = false;
  editingRateId: number = null;
  addRateModel: {
    state: string;
    taxBasis: TaxBasis;
    nexusType: NexusType;
    category: string;
    rate: number;
    active: boolean;
  } = this.blankAddRateModel();

  constructor(
    private _businessSetupService: BusinessSetupService,
    private _connMessageService: ConnMessageService,
    private _abd: AppBaseDataService,
    private _confirmationService: ConfirmationService,
  ) { }

  private taxRateDefaultsLoaded = false;

  isLoadingTaxSettings = false;
  // Set from GetTaxSettings when a record already exists for this business/location — decides
  // CreateTaxSettings (first save, id = 0) vs. UpdateTaxSettings (every save after, real id) in saveFn().
  private existingTaxSettingsId: number = null;

  ngOnInit(): void {
    // Note: the old V1 TaxConfig.tax1 is a flat rate with no associated state, so it's not
    // preloaded here — this screen always pairs a rate with a specific state, and showing a
    // state-less leftover value would be misleading. Pick a state to get a fresh suggested rate.
    this.loadTaxRateDefaults();
    this.loadTaxSettings();
    this.loadStateRates();
  }

  /** Hydrates stateRates from GetTaxRate — the per-state/location rows backing both the
   * "States you collect tax in" (shipping) and "Your business locations" (origin) tables. */
  private loadStateRates(): void {
    this.isLoadingStateRates = true;
    this._businessSetupService.getTaxRate().subscribe({
      next: (res: any) => {
        this.isLoadingStateRates = false;
        const rawData = res?.data ?? res?.Data ?? res;
        const rows: any[] = Array.isArray(rawData) ? rawData : [];
        this.stateRates = rows.map((row: any) => this.mapApiTaxRate(row));
      },
      error: () => {
        this.isLoadingStateRates = false;
      },
    });
  }

  /** Full state name -> ISO 3166-2 two-letter code (e.g. 'Texas' -> 'TX'), for the API payload.
   *  Tolerant of values already in code form. */
  private stateNameToIso2(value: string): string {
    const v = String(value || '').trim();
    if (!v) return '';
    const match = this.usStates.find((s: any) =>
      String(s.abbreviation).toLowerCase() === v.toLowerCase() ||
      String(s.name).toLowerCase() === v.toLowerCase()
    );
    return match ? match.abbreviation : v;
  }

  /** ISO 3166-2 code -> full state name (e.g. 'TX' -> 'Texas'), so the dropdown/table keep showing
   *  full names regardless of whether the backend stored a code or a name. */
  private stateIso2ToName(value: string): string {
    const v = String(value || '').trim();
    if (!v) return '';
    const match = this.usStates.find((s: any) =>
      String(s.abbreviation).toLowerCase() === v.toLowerCase() ||
      String(s.name).toLowerCase() === v.toLowerCase()
    );
    return match ? match.name : v;
  }

  private mapApiTaxRate(row: any): StateTaxRate {
    const basis = String(row?.taxBasis ?? row?.TaxBasis ?? 'shipping').toLowerCase();
    return {
      id: Number(row?.id ?? row?.Id) || 0,
      state: this.stateIso2ToName(row?.state ?? row?.State),
      taxBasis: basis === 'origin' ? 'origin' : 'shipping',
      nexusType: 'Economic',
      category: 'All categories',
      rate: Number(row?.rate ?? row?.Rate ?? 0),
      active: row?.isActive ?? row?.IsActive ?? true,
    };
  }

  /** Hydrates the screen from GetTaxSettings (the TaxSettings table — collectTax + disclaimer only;
   * taxBasis/per-state rates have no backend column yet, see buildTaxSettingsPayload()). Nothing
   * saved yet for this business/location -> default to disabled rather than an empty "on" toggle. */
  private loadTaxSettings(): void {
    this.isLoadingTaxSettings = true;
    this._businessSetupService.getTaxSettings().subscribe({
      next: (res: any) => {
        this.isLoadingTaxSettings = false;
        // data comes back as an array (e.g. one row per location) — use the first row.
        const rawData = res?.data ?? res?.Data ?? res;
        const settings = Array.isArray(rawData) ? rawData[0] : rawData;
        const id = Number(settings?.id ?? settings?.Id) || 0;

        if (!settings || id <= 0) {
          // Nothing came back for this business/location — leave it disabled until configured.
          this.collectTax = false;
          this.existingTaxSettingsId = null;
          return;
        }

        this.existingTaxSettingsId = id;
        this.collectTax = !!(settings?.collectTax ?? settings?.CollectTax);

        const disclaimerAccepted = settings?.disclaimerAccepted ?? settings?.DisclaimerAccepted;
        if (disclaimerAccepted !== undefined) {
          this.disclaimerAcknowledged = !!disclaimerAccepted;
        }
      },
      error: () => {
        this.isLoadingTaxSettings = false;
        // No saved settings / endpoint not reachable — default to disabled rather than showing a
        // half-configured "on" state.
        this.collectTax = false;
        this.existingTaxSettingsId = null;
      },
    });
  }

  /** DisclaimerAccepted/DisclaimerAcceptedAt persist immediately on toggle, same as Collect tax —
   * no reason to make the merchant hit Save just to record that they checked this box. */
  onDisclaimerAcknowledgedChange(value: boolean): void {
    this.disclaimerAcknowledged = value;
    this.persistTaxSettings(value ? 'Disclaimer acknowledged.' : 'Disclaimer acknowledgment removed.');
  }

  /** Loads once and caches — safe to call again from openAddRate()/editRate() to guarantee it's ready before the modal opens. */
  private loadTaxRateDefaults(): void {
    if (this.taxRateDefaultsLoaded || this.isLoadingTaxRateDefaults) {
      return;
    }
    this.isLoadingTaxRateDefaults = true;
    this._businessSetupService.getTaxRateDefault().subscribe({
      next: (res: any) => {
        this.isLoadingTaxRateDefaults = false;
        const rows: any[] = Array.isArray(res) ? res : (res?.data || res?.Data || []);
        const map: { [state: string]: number } = {};
        rows.forEach((row: any) => {
          const state = row?.state || row?.State;
          const rate = row?.combinedRate ?? row?.CombinedRate;
          if (state && rate !== undefined && rate !== null) {
            map[state] = Number(rate);
          }
        });
        this.taxRateDefaultsByState = map;
        // Only cache as "loaded" once we actually got defaults. An empty response happens on a
        // first-time/unconfigured business (before Collect tax is turned on); caching that empty
        // result would make the guard above block every later re-fetch, so the Add-a-tax modal
        // would never get a suggested rate until a full page reload re-ran ngOnInit.
        this.taxRateDefaultsLoaded = Object.keys(map).length > 0;
        // If the modal was opened while this load was still in flight, fill in the suggestion now.
        this.applyPendingRateSuggestion();
      },
      error: () => {
        this.isLoadingTaxRateDefaults = false;
        // Leave taxRateDefaultsLoaded false so the next openAddRate()/editRate() retries the fetch.
      },
    });
  }

  /** Re-applies the suggested default to an already-open Add/Edit modal once defaults finish
   *  loading (the load can still be in flight when the modal opens). onAddRateStateChange itself
   *  guards against overwriting a rate the merchant has manually typed. */
  private applyPendingRateSuggestion(): void {
    if (this.showAddRateModal && this.addRateModel.state) {
      this.onAddRateStateChange(this.addRateModel.state);
    }
  }

  // Tracks whether the current rate value came from auto-suggestion (vs. a manual edit) — lets
  // switching the state re-suggest the new state's rate, while a manual edit is never overwritten.
  private addRateRateIsAutoSuggested = true;

  onAddRateStateChange(state: string): void {
    if (this.addRateModel.rate === null || this.addRateModel.rate === undefined || this.addRateRateIsAutoSuggested) {
      const defaultRate = this.taxRateDefaultsByState[state];
      if (defaultRate !== undefined) {
        this.addRateModel.rate = this.roundToTwoDecimals(defaultRate);
        this.addRateRateIsAutoSuggested = true;
      }
    }
  }

  roundToTwoDecimals(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  /** True when the picked state already has a saved rate row under the same tax basis
   *  (shipping vs. origin are separate lists — see visibleStateRates). Excludes the row
   *  currently being edited so re-saving the same state doesn't flag itself as a duplicate. */
  get isDuplicateState(): boolean {
    const state = this.addRateModel.state;
    if (!state) return false;
    return this.stateRates.some(r =>
      r.taxBasis === this.addRateModel.taxBasis &&
      r.id !== this.editingRateId &&
      r.state.toLowerCase() === state.toLowerCase()
    );
  }

  onAddRateRateInput(): void {
    this.addRateRateIsAutoSuggested = false;
  }

  private blankAddRateModel() {
    return { state: null, taxBasis: this.taxBasis, nexusType: 'Economic' as NexusType, category: 'All categories', rate: null, active: true };
  }

  // TODO re-enable: hidden per request, keep logic intact
  private readonly TAX_BASIS_WARNING_ENABLED = false;

  get taxBasisWarning(): string | null {
    if (!this.TAX_BASIS_WARNING_ENABLED) return null;
    if (this.taxBasis !== 'origin') return null;
    return 'Applies only if your home state uses origin-based sourcing (e.g. Texas, Illinois, Ohio). Confirm that before relying on it.';
  }

  taxBasisLabel(basis: TaxBasis): string {
    return basis === 'origin' ? 'Business location' : "Customer's shipping address";
  }

  /** Only the rows matching the currently selected method — shipping and origin are different tax
   * bases and showing them merged in one table was confusing (it looked like duplicate/conflicting
   * rates for the same state). Switching the selector doesn't delete the other mode's rows. */
  get visibleStateRates(): StateTaxRate[] {
    return this.stateRates.filter(r => r.taxBasis === this.taxBasis);
  }

  openAddRate() {
    this.loadTaxRateDefaults();
    this.editingRateId = null;
    this.addRateModel = this.blankAddRateModel();
    this.addRateRateIsAutoSuggested = true;
    this.showAddRateModal = true;
  }

  editRate(row: StateTaxRate) {
    this.loadTaxRateDefaults();
    this.editingRateId = row.id;
    this.addRateModel = {
      state: row.state,
      taxBasis: row.taxBasis,
      nexusType: row.nexusType,
      category: row.category,
      rate: row.rate,
      active: row.active,
    };
    // Editing an existing row's saved rate is a manual value, not a suggestion — don't let a
    // state change in the modal silently overwrite it.
    this.addRateRateIsAutoSuggested = false;
    this.showAddRateModal = true;
  }

  closeAddRateModal() {
    this.showAddRateModal = false;
  }

  get isAddRateValid(): boolean {
    return !!this.addRateModel.state
      && this.addRateModel.rate !== null && this.addRateModel.rate !== undefined && this.addRateModel.rate >= 0
      && !this.isDuplicateState;
  }

  isSavingRate = false;

  saveRate() {
    if (!this.isAddRateValid || this.isSavingRate) return;

    const payload = {
      id: this.editingRateId || 0,
      country: 'US',
      state: this.addRateModel.state,
      stateCode: this.stateNameToIso2(this.addRateModel.state),
      rate: this.roundToTwoDecimals(this.addRateModel.rate),
      isActive: this.addRateModel.active,
      taxBasis: this.addRateModel.taxBasis,
    };

    const isUpdate = this.editingRateId != null;
    const request = isUpdate
      ? this._businessSetupService.updateTaxRate(payload)
      : this._businessSetupService.createTaxRate(payload);

    this.isSavingRate = true;
    this._abd.showSpinner();
    request.subscribe({
      next: (res: any) => {
        this.isSavingRate = false;
        this._abd.hideSpinner();
        if (res?.success === false) {
          this._connMessageService.showToastMessage(res?.message || 'Failed to save the state rate.', 'error');
          return;
        }

        const rawData = res?.data ?? res?.Data;
        const savedRow = Array.isArray(rawData) ? rawData[0] : rawData;
        const mapped = this.mapApiTaxRate({ ...payload, ...(savedRow || {}) });

        if (isUpdate) {
          const idx = this.stateRates.findIndex(r => r.id === this.editingRateId);
          if (idx > -1) {
            this.stateRates[idx] = mapped;
          }
        } else {
          this.stateRates.push(mapped);
        }

        this._connMessageService.showToastMessage('State tax rate saved.', 'success');
        this.showAddRateModal = false;
      },
      error: () => {
        this.isSavingRate = false;
        this._abd.hideSpinner();
        this._connMessageService.showToastMessage('Error saving the state tax rate!', 'error');
      },
    });
  }

  removeRate(row: StateTaxRate) {
    this._confirmationService.confirm({
      message: `Remove the tax rate row for ${row.state}? This cannot be undone.`,
      header: 'Remove State Rate',
      icon: 'pi pi-info-circle',
      accept: () => {
        this._abd.showSpinner();
        this._businessSetupService.deleteTaxRate(row.id).subscribe({
          next: (res: any) => {
            this._abd.hideSpinner();
            if (res?.success === false) {
              this._connMessageService.showToastMessage(res?.message || 'Failed to remove the state rate.', 'error');
              return;
            }
            this.stateRates = this.stateRates.filter(r => r.id !== row.id);
            this._connMessageService.showToastMessage('State tax rate removed.', 'success');
          },
          error: () => {
            this._abd.hideSpinner();
            this._connMessageService.showToastMessage('Error removing the state tax rate!', 'error');
          },
        });
      },
    });
  }

  cancelButtonClick() {
    this._businessSetupService.showTaxSetting = false;
  }

  saveFn() {
    // Disclaimer block is hidden (TAX_DISCLAIMER_ENABLED) — don't block saving on a checkbox the
    // merchant has no way to see or check.
    if (this.disclaimerEnabled && this.collectTax && !this.disclaimerAcknowledged) {
      this._connMessageService.showToastMessage('Please acknowledge the disclaimer before saving.', 'error');
      return;
    }

    if (this.collectTax && !this.visibleStateRates.length) {
      this._connMessageService.showToastMessage(
        this.taxBasis === 'origin' ? 'Add at least one business location before saving.' : 'Add at least one state before saving.',
        'error'
      );
      return;
    }

    this.persistTaxSettings('Tax settings saved successfully!');
  }

  /** Collect tax still persists immediately (no Save button) once confirmed — but the switch is
   * bound one-way ([ngModel], not [(ngModel)]) so PrimeNG's visual flip doesn't jump ahead of the
   * user's answer. We only assign `collectTax` (which the switch reads from) after they confirm;
   * on Cancel the switch simply never flips. */
  onCollectTaxToggleRequested(event: { checked: boolean }): void {
    const nextValue = event.checked;
    this._confirmationService.confirm({
      message: nextValue
        ? 'Turn on tax collection? Every order will start charging tax based on the rates configured below.'
        : 'Turn off tax collection? Every order will stop charging tax, regardless of the rates configured below.',
      header: nextValue ? 'Turn on Collect Tax' : 'Turn off Collect Tax',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.collectTax = nextValue;
        this.persistTaxSettings(nextValue ? 'Tax collection turned on.' : 'Tax collection turned off.');
      },
    });
  }

  /** First save for this business/location -> CreateTaxSettings (id 0); every save after, once
   * GetTaxSettings/a prior create returned a real id -> UpdateTaxSettings. */
  private persistTaxSettings(successMessage: string): void {
    const payload = this.buildTaxSettingsPayload();
    const isUpdate = this.existingTaxSettingsId != null && this.existingTaxSettingsId > 0;
    const request = isUpdate
      ? this._businessSetupService.updateTaxSettings(payload)
      : this._businessSetupService.createTaxSettings(payload);

    this._abd.showSpinner();
    request.subscribe({
      next: (res: any) => {
        this._abd.hideSpinner();
        if (res?.success !== false) {
          const rawData = res?.data ?? res?.Data;
          const savedRow = Array.isArray(rawData) ? rawData[0] : rawData;
          const savedId = Number(savedRow?.id ?? savedRow?.Id ?? res?.id ?? res?.Id);
          if (savedId > 0) {
            this.existingTaxSettingsId = savedId;
          }
          this._connMessageService.showToastMessage(successMessage, 'success');
        } else {
          this._connMessageService.showToastMessage(res?.message || 'Failed to save tax settings.', 'error');
        }
      },
      error: () => {
        this._abd.hideSpinner();
        this._connMessageService.showToastMessage('Error saving tax settings!', 'error');
      }
    });
  }

  private resolveBusinessId(): number {
    const businessSetup = GlobalService.userInfo?.businessSetup?.[0];
    return Number(businessSetup?.businessId || businessSetup?.id) || 0;
  }

  private resolveLocation(): { locationId: number; locationName: string } {
    const selected = this._abd.selectedLocation;
    const businessSetup = GlobalService.userInfo?.businessSetup?.[0];
    const fallback = Array.isArray(businessSetup?.locations) ? businessSetup.locations[0] : null;
    const location = selected || fallback;
    return {
      locationId: Number(location?.locationId ?? location?.LocationId ?? location?.id ?? location?.Id) || 0,
      locationName: String(location?.locationName ?? location?.LocationName ?? '').trim(),
    };
  }

  /**
   * Matches the backend TaxSettings model exactly: { Id, BusinessId, CreatedByUserId, CollectTax,
   * DisclaimerAccepted, DisclaimerAcceptedAt, LocationId, LocationName } — CreatedAt/UpdatedAt are
   * server-managed and not sent. There is no TaxBasis or per-state rate column on this model yet,
   * so `taxBasis` and `stateRates` stay client-side only (see the "not backed by server" note on
   * the states table) until that schema exists.
   */
  private buildTaxSettingsPayload(): any {
    const location = this.resolveLocation();
    return {
      id: this.existingTaxSettingsId || 0,
      businessId: this.resolveBusinessId(),
      createdByUserId: String(GlobalService.userInfo?.userID || GlobalService.userInfo?.userId || ''),
      collectTax: this.collectTax,
      disclaimerAccepted: this.disclaimerAcknowledged,
      disclaimerAcceptedAt: this.disclaimerAcknowledged ? new Date().toISOString() : null,
      locationId: location.locationId,
      locationName: location.locationName,
    };
  }
}
